import { useEffect, useState } from 'react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { Package, CurrencyJpy, ShieldWarning, WifiHigh } from '@phosphor-icons/react';
import { itApi } from './itApi';

const STATUS_META = [
  { key: 'in_use', label: '使用中', color: '#0052FF' },
  { key: 'in_stock', label: '在庫', color: '#a1a1aa' },
  { key: 'repair', label: '修理中', color: '#f59e0b' },
  { key: 'disposed', label: '廃棄', color: '#ef4444' },
];
const BAR_COLORS = ['#0052FF', '#3A6FFF', '#5B86FF', '#7C9CFF', '#9DB4FF', '#BBCBFF', '#D2DCFF', '#E6EEFF'];
const yen = (n) => (typeof n === 'number' ? `¥${n.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}` : '—');

const Card = ({ title, children, className = '', testid }) => (
  <div className={`bg-white rounded-xl border border-zinc-200 shadow-sm p-5 ${className}`} data-testid={testid}>
    {title && <h2 className="text-sm font-bold text-zinc-900 mb-4">{title}</h2>}
    {children}
  </div>
);

const Kpi = ({ Icon, label, value, sub, accent, testid }) => (
  <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4 flex items-start gap-3" data-testid={testid}>
    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${accent}`}>
      <Icon size={20} weight="duotone" />
    </span>
    <div className="min-w-0">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className="text-xl font-bold text-zinc-900 tabular-nums truncate">{value}</p>
      {sub && <p className="text-[11px] text-zinc-400 truncate">{sub}</p>}
    </div>
  </div>
);

export default function AssetDashboard({ assetType = 'durable' }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setStats(null);
    setErr(false);
    itApi.assetStats({ asset_type: assetType }).then(setStats).catch(() => setErr(true));
  }, [assetType]);

  if (err) return <p className="text-sm text-red-600" data-testid="asset-dashboard-error">統計の取得に失敗しました。</p>;
  if (!stats) return <p className="text-sm text-zinc-400">読み込み中...</p>;
  if (stats.total === 0) {
    return (
      <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center" data-testid="asset-dashboard-empty">
        <Package size={40} weight="duotone" className="text-zinc-300 mx-auto mb-3" />
        <p className="text-sm font-semibold text-zinc-700">まだ資産が登録されていません</p>
        <p className="text-xs text-zinc-400 mt-1">資産を登録すると、ここに分析が表示されます</p>
      </div>
    );
  }

  const statusData = STATUS_META
    .map((s) => ({ ...s, value: stats.by_status[s.key] || 0 }))
    .filter((s) => s.value > 0);
  const categoryData = (stats.by_category || []).slice(0, 8).map((c) => ({ name: c.category, count: c.count }));
  const locationData = (stats.by_location || []).map((l) => ({ name: l.location, count: l.count }));
  const cost = stats.total_cost || 0;
  const book = stats.total_book_value || 0;
  const accum = stats.accumulated_depreciation || 0;
  const bookPct = cost ? Math.round((book / cost) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="asset-dashboard">
      {/* Value KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="asset-kpi-cost" Icon={CurrencyJpy} label="取得価額 合計" value={yen(cost)} accent="bg-blue-100 text-blue-600" />
        <Kpi testid="asset-kpi-book" Icon={CurrencyJpy} label="現在簿価（定額法）" value={yen(book)} sub={`取得比 ${bookPct}%`} accent="bg-emerald-100 text-emerald-600" />
        <Kpi testid="asset-kpi-accum" Icon={CurrencyJpy} label="減価償却 累計" value={yen(accum)} accent="bg-amber-100 text-amber-600" />
        <Kpi testid="asset-kpi-network" Icon={WifiHigh} label="ネットワーク資産（IP割当）" value={`${stats.with_ip} 台`} accent="bg-indigo-100 text-indigo-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status donut */}
        <Card title="ステータス別" testid="asset-chart-status">
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="55%" height={220}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="label" cx="50%" cy="50%"
                  innerRadius={55} outerRadius={85} paddingAngle={2} strokeWidth={0}>
                  {statusData.map((s) => <Cell key={s.key} fill={s.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} 台`, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {statusData.map((s) => (
                <div key={s.key} className="flex items-center justify-between text-sm" data-testid={`asset-legend-${s.key}`}>
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm" style={{ background: s.color }} />
                    <span className="text-zinc-600">{s.label}</span>
                  </span>
                  <span className="font-semibold text-zinc-900 tabular-nums">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Book value vs depreciation */}
        <Card title="資産価値（取得価額 → 現在簿価）" testid="asset-chart-value">
          <div className="space-y-3 pt-2">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">取得価額</span>
              <span className="font-bold text-zinc-900 tabular-nums">{yen(cost)}</span>
            </div>
            <div className="h-7 rounded-lg bg-zinc-100 overflow-hidden flex">
              <div className="h-full bg-teal-700 flex items-center justify-end pr-2"
                style={{ width: `${Math.max(bookPct, 4)}%` }}>
                <span className="text-[11px] text-white font-semibold">{bookPct}%</span>
              </div>
            </div>
            <div className="flex justify-between text-sm pt-1">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-teal-700" /><span className="text-zinc-600">現在簿価</span></span>
              <span className="font-semibold text-emerald-600 tabular-nums">{yen(book)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-zinc-300" /><span className="text-zinc-600">減価償却累計</span></span>
              <span className="font-semibold text-amber-600 tabular-nums">{yen(accum)}</span>
            </div>
            <p className="text-[11px] text-zinc-400 pt-1 border-t border-zinc-200">※ 取得価額・耐用年数・購入日が登録された資産のみ簿価を計算しています。</p>
          </div>
        </Card>

        {/* Category bar */}
        <Card title="カテゴリ別" testid="asset-chart-category">
          <ResponsiveContainer width="100%" height={Math.max(180, categoryData.length * 38)}>
            <BarChart data={categoryData} layout="vertical" margin={{ left: 8, right: 24 }}>
              <CartesianGrid horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#334155' }} />
              <Tooltip formatter={(v) => [`${v} 台`, '台数']} cursor={{ fill: '#f8fafc' }} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                {categoryData.map((c, i) => <Cell key={c.name || i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Location bar */}
        <Card title="設置場所別" testid="asset-chart-location">
          <ResponsiveContainer width="100%" height={Math.max(180, locationData.length * 38)}>
            <BarChart data={locationData} layout="vertical" margin={{ left: 8, right: 24 }}>
              <CartesianGrid horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#334155' }} />
              <Tooltip formatter={(v) => [`${v} 台`, '台数']} cursor={{ fill: '#f8fafc' }} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]} fill="#0052FF" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {stats.warranty_expiring_soon > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3" data-testid="asset-warranty-alert">
          <ShieldWarning size={22} weight="duotone" className="text-rose-500 shrink-0" />
          <p className="text-sm text-rose-700">
            保証期限が <span className="font-bold">60日以内</span> の資産が <span className="font-bold">{stats.warranty_expiring_soon} 台</span> あります。
          </p>
        </div>
      )}
    </div>
  );
}
